/**
 * Review Service
 * Handles reviews for all roles (contractors, verifiers, inspectors) with API integration
 * Falls back to localStorage for backward compatibility
 */

import api from './api';

const REVIEWS_KEY = 'buildzim_reviews';
const REVIEW_COUNTER_KEY = 'buildzim_review_counter';
const normalizeReview = (review = {}) => {
  const reviewerName = review.reviewer_name
    || [review.reviewer_first_name, review.reviewer_last_name].filter(Boolean).join(' ')
    || review.clientName
    || 'Anonymous reviewer';

  const responseText = review.response_text || review.verifier_response || review.response?.message || null;
  const responseDate = review.updated_at || review.verifier_response_date || review.response?.date || null;

  return {
    ...review,
    clientId: review.clientId || review.reviewer_id,
    reviewerName,
    clientName: reviewerName,
    comment: review.comment || review.review_text || review.reviewText || '',
    reviewText: review.review_text || review.reviewText || review.comment || '',
    projectTitle: review.project_title || review.projectTitle || (review.project_id ? `Project ${review.project_id}` : 'Completed service'),
    createdAt: review.created_at || review.createdAt || review.date,
    date: review.created_at || review.date || review.createdAt,
    verified: Boolean(review.is_verified ?? review.is_verified_review ?? review.verified),
    response: responseText ? {
      message: responseText,
      date: responseDate,
      createdAt: responseDate
    } : null,
    reviewType: review.review_type || (review.verifier_id ? 'verifier_review' : 'user_review'),
    appeal_filed: Boolean(review.appeal_filed)
  };
};

// Initialize review counter
const getReviewCounter = () => {
  const counter = localStorage.getItem(REVIEW_COUNTER_KEY);
  return counter ? parseInt(counter) : 1000;
};

const setReviewCounter = (counter) => {
  localStorage.setItem(REVIEW_COUNTER_KEY, counter.toString());
};

// Utility functions
const getAllReviews = () => {
  const reviews = localStorage.getItem(REVIEWS_KEY);
  return reviews ? JSON.parse(reviews) : [];
};

const saveReviews = (reviews) => {
  localStorage.setItem(REVIEWS_KEY, JSON.stringify(reviews));
};

const generateReviewId = () => {
  const counter = getReviewCounter();
  setReviewCounter(counter + 1);
  return `review_${counter}`;
};

// Simulate network delay
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Initialize with sample data if empty
const initializeSampleReviews = () => {
  const existingReviews = getAllReviews();
  if (existingReviews.length === 0) {
    const sampleReviews = [
      {
        id: 'review_001',
        contractorId: 'user_002', // James Mukamuri
        clientId: 'user_001', // Margaret Chivero
        clientName: 'Margaret Chivero',
        projectId: 'proj_001',
        projectTitle: 'Luxury Villa - Borrowdale',
        rating: 5,
        comment: 'Exceptional work quality and professionalism. Delivered on time and within budget.',
        date: '2023-12-15',
        createdAt: new Date('2023-12-15').toISOString(),
        verified: true,
        helpful: 12,
        reported: false,
        response: null // contractor response
      },
      {
        id: 'review_002',
        contractorId: 'user_002',
        clientId: 'user_004', // Mock client
        clientName: 'Peter Ncube',
        projectId: 'proj_002',
        projectTitle: 'Residential Extension',
        rating: 5,
        comment: 'Outstanding craftsmanship. Very pleased with the quality of work.',
        date: '2023-05-20',
        createdAt: new Date('2023-05-20').toISOString(),
        verified: true,
        helpful: 8,
        reported: false,
        response: {
          message: 'Thank you for the wonderful review! It was a pleasure working on your project.',
          date: '2023-05-22',
          createdAt: new Date('2023-05-22').toISOString()
        }
      },
      {
        id: 'review_003',
        contractorId: 'user_002',
        clientId: 'user_005', // Mock client
        clientName: 'Corporate Solutions Ltd',
        projectId: 'proj_003',
        projectTitle: 'Office Renovation',
        rating: 4,
        comment: 'Professional service and good quality work. Minor delays but overall satisfied.',
        date: '2023-10-05',
        createdAt: new Date('2023-10-05').toISOString(),
        verified: true,
        helpful: 5,
        reported: false,
        response: null
      }
    ];
    saveReviews(sampleReviews);
  }
};

// Review service
const reviewService = {
  
  // Initialize service
  initialize: () => {
    initializeSampleReviews();
  },

  // Get all reviews for a contractor (or any user)
  getContractorReviews: async (contractorId, options = {}) => {
    try {
      const { page = 1, limit = 10, minRating = null, onlyVerified = false } = options;

      // Fetch from API
      const response = await api.get(`/reviews/user/${contractorId}`, {
        params: { page, limit, minRating, onlyVerified }
      });

      if (response.data.success) {
        return response.data.data.map(normalizeReview);
      }

      return [];
    } catch (error) {
      console.error('Get contractor reviews error:', error);
      
      // Fall back to localStorage
      await delay();
      const allReviews = getAllReviews();
      const contractorReviews = allReviews.filter(review => review.contractorId === contractorId);
      
      // Sort by date (newest first)
      return contractorReviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
  },

  // Get reviews submitted by a client
  getClientReviews: async (clientId) => {
    await delay();
    const allReviews = getAllReviews();
    return allReviews.filter(review => review.clientId === clientId);
  },

  // Submit a new review
  submitReview: async (reviewData) => {
    try {
      // Determine reviewee role from contractorId or role field
      const revieweeRole = reviewData.revieweeRole || 'contractor';
      const revieweeId = reviewData.contractorId || reviewData.revieweeId;

      // Prepare API payload
      const payload = {
        revieweeId: revieweeId,
        revieweeRole: revieweeRole,
        projectId: reviewData.projectId,
        escrowAccountId: reviewData.escrowAccountId,
        verificationRequestId: reviewData.verificationRequestId,
        milestoneId: reviewData.milestoneId || null,
        rating: parseInt(reviewData.rating),
        title: reviewData.title,
        reviewText: reviewData.comment || reviewData.reviewText,
        professionalismRating: reviewData.professionalismRating,
        accuracyRating: reviewData.accuracyRating,
        timelinessRating: reviewData.timelinessRating,
        communicationRating: reviewData.communicationRating,
        qualityRating: reviewData.qualityRating,
        wouldRecommend: reviewData.wouldRecommend
      };

      // Submit to API
      const response = await api.post('/reviews', payload);
      return response.data;

    } catch (error) {
      console.error('Submit review error:', error);
      
      // If API fails, fall back to localStorage for backward compatibility
      if (error.response?.status === 401) {
        throw new Error('You must be logged in to submit a review');
      }
      
      throw new Error(error.response?.data?.message || error.message || 'Failed to submit review');
    }
  },

  // Update an existing review
  updateReview: async (reviewId, updateData) => {
    await delay();
    
    const reviews = getAllReviews();
    const reviewIndex = reviews.findIndex(review => review.id === reviewId);
    
    if (reviewIndex === -1) {
      throw new Error('Review not found.');
    }

    // Update review
    reviews[reviewIndex] = {
      ...reviews[reviewIndex],
      ...updateData,
      updatedAt: new Date().toISOString()
    };

    saveReviews(reviews);

    return {
      success: true,
      data: reviews[reviewIndex],
      message: 'Review updated successfully!'
    };
  },

  // Delete a review
  deleteReview: async (reviewId, userId) => {
    await delay();
    
    const reviews = getAllReviews();
    const review = reviews.find(r => r.id === reviewId);
    
    if (!review) {
      throw new Error('Review not found.');
    }

    // Check if user can delete (only review author or admin)
    if (review.clientId !== userId) {
      throw new Error('You can only delete your own reviews.');
    }

    const filteredReviews = reviews.filter(r => r.id !== reviewId);
    saveReviews(filteredReviews);

    return {
      success: true,
      message: 'Review deleted successfully!'
    };
  },

  // Add contractor response to review
  addContractorResponse: async (reviewId, contractorId, responseMessage) => {
    try {
      // Submit response to API
      const response = await api.post(`/reviews/${reviewId}/response`, { responseText: responseMessage });
      return response.data;

    } catch (error) {
      console.error('Add response error:', error);
      
      // Fall back to localStorage
      if (error.response?.status !== 404) {
        await delay();
        
        const reviews = getAllReviews();
        const reviewIndex = reviews.findIndex(review => review.id === reviewId);
        
        if (reviewIndex === -1) {
          throw new Error('Review not found.');
        }

        // Check if contractor owns this review
        if (reviews[reviewIndex].contractorId !== contractorId) {
          throw new Error('You can only respond to your own reviews.');
        }

        // Add response
        reviews[reviewIndex].response = {
          message: responseMessage.trim(),
          date: new Date().toISOString().split('T')[0],
          createdAt: new Date().toISOString()
        };

        saveReviews(reviews);

        return {
          success: true,
          data: reviews[reviewIndex],
          message: 'Response added successfully!'
        };
      }
      
      throw new Error(error.response?.data?.message || error.message || 'Failed to add response');
    }
  },

  // Mark review as helpful
  markReviewHelpful: async (reviewId, userId) => {
    await delay(200);
    
    const reviews = getAllReviews();
    const reviewIndex = reviews.findIndex(review => review.id === reviewId);
    
    if (reviewIndex === -1) {
      throw new Error('Review not found.');
    }

    // In a real app, you'd track which users marked it helpful
    reviews[reviewIndex].helpful = (reviews[reviewIndex].helpful || 0) + 1;
    saveReviews(reviews);

    return {
      success: true,
      data: reviews[reviewIndex]
    };
  },

  // Report a review
  reportReview: async (reviewId, reason, reporterId, reviewType = 'user_review') => {
    try {
      const response = await api.post(`/reviews/${reviewId}/flag`, { reason, reviewType });
      return response.data;
    } catch (error) {
      console.error('Report review error:', error);
      throw new Error(error.response?.data?.message || 'Failed to report review');
    }
  },

  // Get contractor rating statistics
  getContractorStats: async (contractorId) => {
    try {
      // Fetch from API
      const response = await api.get(`/reviews/user/${contractorId}/stats`);

      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error('Get contractor stats error:', error);
    }
    
    // Fall back to localStorage calculation
    await delay(300);
    const reviews = await reviewService.getContractorReviews(contractorId);
    
    if (reviews.length === 0) {
      return {
        totalReviews: 0,
        averageRating: 0,
        ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        totalHelpful: 0,
        responseRate: 0
      };
    }

    const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
    const averageRating = totalRating / reviews.length;

    const ratingDistribution = reviews.reduce((dist, review) => {
      dist[review.rating] = (dist[review.rating] || 0) + 1;
      return dist;
    }, { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });

    const totalHelpful = reviews.reduce((sum, review) => sum + (review.helpful || 0), 0);
    const reviewsWithResponses = reviews.filter(review => review.response).length;
    const responseRate = Math.round((reviewsWithResponses / reviews.length) * 100);

    return {
      totalReviews: reviews.length,
      averageRating: Math.round(averageRating * 10) / 10,
      ratingDistribution,
      totalHelpful,
      responseRate,
      recentReviews: reviews.slice(0, 3)
    };
  },

  // Search reviews
  searchReviews: async (searchTerm, filters = {}) => {
    await delay(400);
    
    let reviews = getAllReviews();
    
    // Apply text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      reviews = reviews.filter(review => 
        review.comment.toLowerCase().includes(term) ||
        review.clientName.toLowerCase().includes(term) ||
        review.projectTitle.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.contractorId) {
      reviews = reviews.filter(review => review.contractorId === filters.contractorId);
    }

    if (filters.rating) {
      reviews = reviews.filter(review => review.rating === parseInt(filters.rating));
    }

    if (filters.verified !== undefined) {
      reviews = reviews.filter(review => review.verified === filters.verified);
    }

    // Sort reviews
    const sortBy = filters.sortBy || 'date';
    reviews.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'helpful':
          return (b.helpful || 0) - (a.helpful || 0);
        case 'date':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    return reviews;
  },

  // Check if client can review a contractor (based on completed projects)
  canClientReview: async (clientId, contractorId, projectId) => {
    await delay(200);
    
    // Check if project exists and is completed (mock check)
    // In real app, this would check project status from projectService
    
    // Check if review already exists
    const existingReviews = getAllReviews();
    const existingReview = existingReviews.find(
      review => review.projectId === projectId && review.clientId === clientId
    );

    return {
      canReview: !existingReview,
      reason: existingReview ? 'Review already submitted for this project' : null
    };
  },

  // Flag a review for moderation
  flagReview: async (reviewId, reason, reviewType = 'user_review') => {
    try {
      const response = await api.post(`/reviews/${reviewId}/flag`, { reason, reviewType });
      return response.data;
    } catch (error) {
      console.error('Flag review error:', error);
      throw new Error(error.response?.data?.message || 'Failed to flag review');
    }
  },

  // Submit review for verifier
  submitVerifierReview: async (verifierId, reviewData) => {
    return reviewService.submitReview({
      ...reviewData,
      revieweeId: verifierId,
      revieweeRole: 'verifier'
    });
  },

  // Submit review for inspector
  submitInspectorReview: async (inspectorId, reviewData) => {
    return reviewService.submitReview({
      ...reviewData,
      revieweeId: inspectorId,
      revieweeRole: 'quality_inspector'
    });
  },

  // Get reviews for any user role
  getUserReviews: async (userId, options = {}) => {
    try {
      const { page = 1, limit = 10, minRating = null, onlyVerified = false } = options;

      const response = await api.get(`/reviews/user/${userId}`, {
        params: { page, limit, minRating, onlyVerified }
      });

      if (response.data.success) {
        return response.data.data.map(normalizeReview);
      }

      return [];
    } catch (error) {
      console.error('Get user reviews error:', error);
      return [];
    }
  },

  // Get user rating statistics
  getUserStats: async (userId) => {
    try {
      const response = await api.get(`/reviews/user/${userId}/stats`);

      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error('Get user stats error:', error);
    }

    return {
      totalReviews: 0,
      averageRating: 0,
      ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      verifiedReviews: 0,
      responseRate: 0,
      detailedRatings: {}
    };
  }
};

// Initialize sample data when service is loaded
reviewService.initialize();

export default reviewService;
